import { Box, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import {
    OLB_BOOKING_REQUEST_SETTINGS,
    OlbBookingRequestCategory,
    saveOlbBookingRequestCategoriesThunk
} from '@spike/marketplace-action';
import clsx from 'clsx';
import { Switch } from 'components/UI';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import useServiceCategories from 'hooks/useServiceCategories';
import { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';

export interface Props {
    className?: string;
    disabled?: boolean;
}

interface CategoryRow {
    id: OlbBookingRequestCategory;
    label: string;
    enabled: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            display: 'flex',
            alignItems: 'center'
        },
        label: {
            flex: 1,
            marginRight: 15,
            color: '#222222',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '25px',
            [theme.breakpoints.down('sm')]: {
                fontSize: '14px',
                lineHeight: '140%'
            }
        },
        description: {
            color: '#7A7A7A',
            fontSize: '14px',
            lineHeight: '19.6px',
            marginTop: '4px'
        },
        list: {
            marginTop: '20px'
        },
        row: {
            padding: '14px 0px',
            borderBottom: '1px solid #F1F1F1'
        },
        disabled: {
            opacity: 0.5,
            pointerEvents: 'none'
        }
    })
);

export const BookingRequestCategories: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const serviceCategories = useServiceCategories();

    const settings = marketplace.settings ?? [];

    const categories: Array<CategoryRow> = [
        { id: 'grooming', label: 'Grooming Services', enabled: serviceCategories.hasGrooming },
        { id: 'daycare', label: 'Daycare Services', enabled: serviceCategories.hasDaycare },
        { id: 'boarding', label: 'Boarding Services', enabled: serviceCategories.hasBoarding },
        { id: 'training', label: 'Training Services', enabled: serviceCategories.hasTraining },
        { id: 'walking', label: 'Walking Services', enabled: serviceCategories.hasWalking }
    ];

    const isSelected = (category: OlbBookingRequestCategory): boolean =>
        settings.find(setting => setting.key === OLB_BOOKING_REQUEST_SETTINGS[category].key)?.value ===
        SETTINGS_TRUE_VALUE;

    const [expanded, setExpanded] = useState(categories.some(category => isSelected(category.id)));

    const toggleHandler = (checked: boolean) => {
        setExpanded(checked);
        if (!checked) {
            // Clears every backend key, not just the visible rows, so backfilled
            // walking/training/transportation flags don't stay stuck on true
            const cleared = (Object.keys(OLB_BOOKING_REQUEST_SETTINGS) as Array<OlbBookingRequestCategory>).reduce(
                (acc, category) => ({ ...acc, [category]: false }),
                {} as Partial<Record<OlbBookingRequestCategory, boolean>>
            );
            dispatch(saveOlbBookingRequestCategoriesThunk(apiClientWrapper, cleared));
        }
    };

    const changeCategoryHandler = (category: OlbBookingRequestCategory, checked: boolean) => {
        dispatch(saveOlbBookingRequestCategoriesThunk(apiClientWrapper, { [category]: checked }));
    };

    return (
        <Box className={props.className}>
            <Box className={classes.header}>
                <Typography className={classes.label}>Review and approve each appointment request manually</Typography>
                <Switch
                    checked={expanded}
                    disabled={props.disabled}
                    onChange={toggleHandler}
                />
            </Box>
            {expanded && (
                <>
                    <Typography className={classes.description}>
                        Select the service categories that require manual approval
                    </Typography>
                    <Box className={classes.list}>
                        {categories
                            .filter(category => category.enabled)
                            .map(category => (
                                <Box
                                    key={category.id}
                                    className={clsx(classes.row, { [classes.disabled]: props.disabled })}
                                >
                                    <Checkbox
                                        label={category.label}
                                        checked={isSelected(category.id)}
                                        onChange={checked => changeCategoryHandler(category.id, checked)}
                                    />
                                </Box>
                            ))}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default BookingRequestCategories;
